import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type RelationEditor,
  type WithId,
} from '@framework/form/form/relations/useRelationEditor.ts';
import { useCallback, useMemo } from 'react';
import { type IWidgetAdvancedOption } from '../../../../Widget.ts';
import { SortableDiv } from '../../../advanced/table-input/columns/SortableTableColumnItem.tsx';
import { AdvancedSelectOptionValueView } from '../../advanced-select-common/config/AdvancedSelectOptionValueView.tsx';
import { type IAdvancedSelectColumns } from '../../advanced-select-common/IAdvancedSelectColumns.ts';
import { createDefaultAdvancedOptionValue } from './util/createDefaultOptionValue.ts';

export interface IAdvancedSelectSortableWidgetOptionItemProps {
  item: IWidgetAdvancedOption;
  uid: string;
  active: WithId<IWidgetAdvancedOption> | null;
  columns?: IAdvancedSelectColumns[];
  editor: RelationEditor<IWidgetAdvancedOption>;
  incrementIndex: () => void;
}

/**
 * Componente Sortable specifico per le opzioni del widget AdvancedSelect
 */
export function AdvancedSelectSortableWidgetOptionItem(
  props: IAdvancedSelectSortableWidgetOptionItemProps,
) {
  const { uid, active, editor, item, ...otherProps } = props;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: uid });

  const style = useMemo(
    () => ({
      transform: CSS.Translate.toString(transform),
      transition,
      // Lo nascondo se è quello selezionato
      visibility:
        active?.__uid === uid ? ('hidden' as const) : ('visible' as const),
    }),
    [active, transform, transition, uid],
  );

  const onEdit = useCallback(() => {
    editor.handle.open(item);
  }, [editor, item]);

  const onRemove = useCallback(() => {
    editor.remove(item);
  }, [editor, item]);

  const onAdd = useCallback(() => {
    props.incrementIndex();
    editor.handle.open(createDefaultAdvancedOptionValue());
  }, [editor, props]);

  return (
    <SortableDiv ref={setNodeRef} style={style} {...attributes}>
      <AdvancedSelectOptionValueView
        item={item}
        onEdit={onEdit}
        onRemove={onRemove}
        onAdd={onAdd}
        {...otherProps}
        listeners={listeners}
      />
    </SortableDiv>
  );
}
